/**
 * PresentationsApplication - Main application class for PresentationsApp_v2
 * Extends BaseApplication to provide presentation/slideshow functionality
 */

import * as Y from 'yjs';
import { BaseApplication } from '../../../services/baseApplication/BaseApplication';
import { AppConfig, SerializedNode } from '../../../services/baseApplication/types';
import { PRESENTATIONS_APP_CONFIG, SlideLayout, SlideTransition } from './PresentationsConfig';
import { createCollaborationProvider } from '../../../services/collaboration';
import { authService } from '../../../services/auth/authService';
import { entityService } from '../../../services/entityService';
import { generateUserColor } from '../../shared/appHelpers';
import { 
  SlideElement, 
  CreateElementParams, 
  ElementPosition, 
  FormattingRange,
  DEFAULT_FONT_FAMILY,
  DEFAULT_FONT_SIZE,
  DEFAULT_LINE_HEIGHT
} from '../types/slideElements';
import { PresentationTheme, ThemeApplicationOptions } from '../types/theme';

export class PresentationsApplication extends BaseApplication {
  private currentSlideIndex: number = 0;
  public undoManager: Y.UndoManager | null = null;
  // ✅ collaborationProvider inherited from BaseApplication
  
  // Theme support
  private _cachedTheme: PresentationTheme | null = null;
  private _cachedEntity: any = null;

  constructor() {
    super();
    // ✅ REQUIRED: Disable suggestion mode for Presentations
    // Insert/replace operations still check suggestionMode - keep false to avoid marking elements
    this.suggestionMode = false;
  }

  /**
   * Get application configuration
   */
  protected getConfig(): AppConfig {
    return PRESENTATIONS_APP_CONFIG;
  }
  
  /**
   * Load presentation from backend
   * ✅ FIX: Removed unnecessary initialize() call that created duplicate Y.Doc
   */
  async load(entityId: string, options: { offline?: boolean } = {}): Promise<void> {
    console.log('[PresentationsApplication] Loading presentation:', entityId);
    
    const entity = await entityService.get(entityId);
    if (!entity) throw new Error(`Presentation not found: ${entityId}`);
    
    // ✅ Cache entity for theme access
    this._cachedEntity = entity;
    
    // ✅ THEME FIX: Cache theme from entity if it exists
    if (entity.skeleton?.theme) {
      this._cachedTheme = entity.skeleton.theme;
      console.log('[PresentationsApplication] ✅ Theme loaded from entity:', {
        hasTheme: !!this._cachedTheme,
        fontFamily: this._cachedTheme?.defaults.fontFamily
      });
    }
    
    // ✅ FIX: Don't call initialize() - just set entityId directly
    // initialize() creates a Y.Doc that gets discarded (memory leak!)
    this.entityId = entityId;
    
    // ✅ NEW PATTERN: Create own Y.Doc (self-contained)
    this.ydoc = new Y.Doc();
    
    // ✅ NEW PATTERN: Load base64 state directly (if exists)
    if (entity.skeleton?.base64 && this.ydoc) {
      try {
        console.log('[PresentationsApplication] 📥 Loading Y.js state from base64...', {
          hasBase64: !!entity.skeleton.base64,
          base64Length: entity.skeleton.base64.length,
          base64Preview: entity.skeleton.base64.substring(0, 100) + '...',
          entityId: entity._id
        });
        this.loadBase64State(entity.skeleton.base64);
        console.log('[PresentationsApplication] ✅ Y.js state loaded');
      } catch (error) {
        // ✅ FIX (Bug #7): Better error handling for corrupted state
        console.error('[PresentationsApplication] ❌ Failed to load Y.js state:', error);
        this.emit('loadError', { 
          error, 
          message: 'Failed to load presentation data. Starting with empty presentation.' 
        });
        // Continue with empty presentation rather than failing completely
        console.log('[PresentationsApplication] ⚠️ Continuing with empty presentation');
      }
    } else {
      console.log('[PresentationsApplication] 📥 NO BASE64 STATE FOUND IN ENTITY!', {
        hasEntity: !!entity,
        hasSkeleton: !!entity.skeleton,
        hasBase64: !!entity.skeleton?.base64,
        skeletonKeys: entity.skeleton ? Object.keys(entity.skeleton) : [],
        entityId: entity._id,
        entityName: entity.name
      });
    }
    
    if (this.ydoc) {
      this.yxml = this.ydoc.getXmlFragment('prosemirror');
      
      console.log('[PresentationsApplication] Y.xml loaded:', {
        yxmlLength: this.yxml.length,
        yxmlType: this.yxml.constructor.name,
        hasContent: this.yxml.length > 0
      });
      
      // ✅ NEW: Create UndoManager for Y.js tracking
      this.undoManager = new Y.UndoManager([this.yxml], {
        trackedOrigins: new Set([this.ydoc.clientID]),
        captureTimeout: 300,
      });
      console.log('[PresentationsApplication] ✅ UndoManager created');
      
      if (this.yxml.length === 0 && !entity.skeleton?.base64) {
        this.initializeEmptyPresentation();
      }
      
      // ✅ FIX (Issue #5): Setup auto-save BEFORE building indices
      // This ensures migrations get auto-saved
      console.log('[PresentationsApplication] 🔧 Setting up auto-save...');
      this.setupAutoSave();
      console.log('[PresentationsApplication] ✅ Auto-save enabled');
      
      await this.buildCoreIndices();
      
      console.log('[PresentationsApplication] Indices built:', {
        byIdIndexSize: this.byIdIndex.size,
        byClassIndexSize: this.byClassIndex.size,
        slideClassEntries: this.byClassIndex.get('slide')?.size || 0
      });
    }
    
    if (!options.offline && this.ydoc) {
      await this.setupCollaborationWithDoc(entityId, this.ydoc!);
    }
    
    console.log('[PresentationsApplication] ✅ Presentation loaded');
  }
  
  /**
   * Set up collaboration with proper listener cleanup
   * ✅ FIX: Prevent memory leaks by cleaning up listeners and timeout
   */
  private async setupCollaborationWithDoc(entityId: string, ydoc: Y.Doc): Promise<void> {
    const authUser = authService.getCurrentUser();
    if (!authUser) {
      throw new Error('Not authenticated');
    }
    
    const currentUser = {
      id: authUser.id,
      name: authUser.name || authUser.email || 'User',
      color: generateUserColor(authUser.id)
    };
    
    this.collaborationProvider = createCollaborationProvider({
      documentId: `doc:${entityId}`,
      user: currentUser,
      websocketUrl: this.getWebSocketUrl(),
      authToken: '',
      offline: false,
      doc: ydoc,
    });
    
    this.collaborationProvider.connect();
    
    // ✅ FIX: Properly cleanup timeout and event listeners to prevent memory leaks
    await new Promise<void>((resolve) => {
      let timeoutId: NodeJS.Timeout | null = null;
      let syncedHandler: (() => void) | null = null;
      let errorHandler: ((error: Error) => void) | null = null;
      
      // ✅ Cleanup function to remove all listeners and timeout
      const cleanup = () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        if (syncedHandler && this.collaborationProvider) {
          this.collaborationProvider.off('synced', syncedHandler);
          syncedHandler = null;
        }
        if (errorHandler && this.collaborationProvider) {
          this.collaborationProvider.off('error', errorHandler);
          errorHandler = null;
        }
      };
      
      // Timeout handler
      timeoutId = setTimeout(() => {
        console.warn('[PresentationsApplication] Sync timeout - proceeding anyway');
        cleanup();
        resolve();
      }, 5000);
      
      // Success handler
      syncedHandler = () => {
        console.log('[PresentationsApplication] ✅ Presentation synced');
        cleanup();
        resolve();
      };
      
      // Error handler
      errorHandler = (error: Error) => {
        console.error('[PresentationsApplication] Sync error:', error);
        cleanup();
        resolve(); // Don't fail - continue with local state
      };
      
      // Attach handlers
      this.collaborationProvider!.on('synced', syncedHandler);
      this.collaborationProvider!.on('error', errorHandler);
    });
    
    console.log('[PresentationsApplication] ✅ Collaboration ready');
  }
  
  /**
   * Initialize empty presentation with first slide
   */
  private initializeEmptyPresentation(): void {
    if (!this.yxml || !this.ydoc) {
      console.warn('[PresentationsApplication] Cannot initialize - yxml or ydoc missing');
      return;
    }
    
    // ✅ FIX: Wrap in transaction with clientID for undo tracking
    this.ydoc.transact(() => {
      // Create first slide
      const slide = new Y.XmlElement('slide');
      slide.setAttribute('id', this.generateId());
      slide.setAttribute('className', 'slide');
      slide.setAttribute('semantic', JSON.stringify({ slideNumber: 1 }));
      
      // ✅ FIX: Add non-null assertion since we checked above
      this.yxml!.insert(0, [slide]);
    }, this.ydoc.clientID);
    
    console.log('[PresentationsApplication] Empty presentation initialized with 1 slide');
  }
  
  /**
   * Generate unique ID
   */

  /**
   * Get Y.XmlFragment for direct access (needed for UI operations)
   */
  public getYXml(): Y.XmlFragment | undefined {
    return this.yxml;
  }
  
  /**
   * Expose buildCoreIndices for UI sync operations
   * ✅ NEEDED: UI needs to rebuild indices after manual Y.js operations
   */
  public async rebuildIndices(): Promise<void> {
    await this.buildCoreIndices();
  }
  
  /**
   * Presentations-specific: Get all slides
   */
  public getAllSlides(): SerializedNode[] {
    const slideIds = this.byClassIndex.get('slide');
    if (!slideIds) return [];

    const slides = Array.from(slideIds)
      .map(id => {
        const node = this.byIdIndex.get(id);
        if (!node || !(node instanceof Y.XmlElement)) return null;
        const ref = node as Y.XmlElement;
        
        try {
          const semanticStr = ref.getAttribute('semantic');
          let semantic: any = undefined;
          
          // Parse semantic JSON if present
          if (semanticStr) {
            try {
              semantic = JSON.parse(semanticStr as string);
            } catch (e) {
              console.warn(`[getAllSlides] Failed to parse semantic for slide ${id}:`, e);
            }
          }
          
          const serialized: SerializedNode = {
            id: ref.getAttribute('id') as string,
            type: ref.nodeName,
            className: ref.getAttribute('className') as string,
            attrs: {
              semantic
            },
            content: [] // We'll populate this if needed
          };
          
          return serialized;
        } catch (error) {
          console.error(`[getAllSlides] ❌ Serialization failed for id ${id}:`, error);
          return null;
        }
      })
      .filter((node): node is SerializedNode => node !== null);

    // Sort by slide number
    return slides.sort((a, b) => {
      const numA = a.attrs?.semantic?.slideNumber || 0;
      const numB = b.attrs?.semantic?.slideNumber || 0; // ✅ FIX: Use 'b' not 'a'
      return numA - numB;
    });
  }

  /**
   * Presentations-specific: Get slide by number
   */
  public getSlideByNumber(slideNumber: number): SerializedNode | undefined {
    const slides = this.getAllSlides();
    return slides.find(slide => slide.attrs?.semantic?.slideNumber === slideNumber);
  }

  /**
   * Presentations-specific: Get slide content (elements within a slide)
   */
  public getSlideContent(slideNumber: number): SerializedNode[] {
    const slide = this.getSlideByNumber(slideNumber);
    if (!slide || !slide.content) return [];

    return slide.content;
  }

  /**
   * Presentations-specific: Get current slide
   */
  public getCurrentSlide(): SerializedNode | undefined {
    return this.getSlideByNumber(this.currentSlideIndex + 1);
  }

  /**
   * ✅ NEW: Set current slide index (for UI navigation)
   * CRITICAL FIX: Allows UI to synchronize with service state
   */
  public setCurrentSlideIndex(index: number): void {
    console.log('[PresentationsApplication] Setting current slide index:', index);
    this.currentSlideIndex = index;
  }

  /**
   * ✅ NEW: Get current slide index
   */
  public getCurrentSlideIndex(): number {
    return this.currentSlideIndex;
  }

  /**
   * Presentations-specific: Navigate to slide
   */
  public goToSlide(slideNumber: number): boolean {
    const slides = this.getAllSlides();
    if (slideNumber < 1 || slideNumber > slides.length) return false;

    this.currentSlideIndex = slideNumber - 1;
    return true;
  }

  /**
   * Presentations-specific: Next slide
   */
  public nextSlide(): boolean {
    const slides = this.getAllSlides();
    if (this.currentSlideIndex >= slides.length - 1) return false;

    this.currentSlideIndex++;
    return true;
  }

  /**
   * Presentations-specific: Previous slide
   */
  public previousSlide(): boolean {
    if (this.currentSlideIndex <= 0) return false;

    this.currentSlideIndex--;
    return true;
  }

  /**
   * Presentations-specific: Get presentation statistics
   */
  public getPresentationStats() {
    const slides = this.getAllSlides();
    
    return {
      totalSlides: slides.length,
      currentSlide: this.currentSlideIndex + 1,
      titleSlides: slides.filter(s => s.attrs?.semantic?.layout === SlideLayout.TITLE).length,
      transitions: slides.filter(s => s.attrs?.semantic?.transition !== SlideTransition.NONE).length,
      slidesWithNotes: slides.filter(s => s.attrs?.semantic?.notes).length,
    };
  }

  /**
   * Presentations-specific: Get slide with most content
   */
  public getSlidesWithMostContent(): SerializedNode[] {
    const slides = this.getAllSlides();
    
    const slidesWithCount = slides.map(slide => ({
      slide,
      elementCount: slide.content?.length || 0
    }));

    slidesWithCount.sort((a, b) => b.elementCount - a.elementCount);
    return slidesWithCount.slice(0, 5).map(item => item.slide);
  }

  /**
   * Presentations-specific: Search across all slide content
   */
  public searchSlides(query: string): Array<{ slide: SerializedNode; matches: SerializedNode[] }> {
    const slides = this.getAllSlides();
    const results: Array<{ slide: SerializedNode; matches: SerializedNode[] }> = [];

    for (const slide of slides) {
      const matches: SerializedNode[] = [];
      
      if (slide.content) {
        for (const element of slide.content) {
          const text = this.extractTextFromNode(element);
          if (text.toLowerCase().includes(query.toLowerCase())) {
            matches.push(element);
          }
        }
      }

      if (matches.length > 0) {
        results.push({ slide, matches });
      }
    }

    return results;
  }

  /**
   * Presentations-specific: Export to plain text (speaker notes)
   */
  public exportToText(): string {
    const slides = this.getAllSlides();
    const lines: string[] = [];

    for (const slide of slides) {
      const slideNum = slide.attrs?.semantic?.slideNumber || 0;
      lines.push(`=== SLIDE ${slideNum} ===`);
      lines.push('');

      // Extract title
      if (slide.content) {
        for (const element of slide.content) {
          if (element.attrs?.className === 'slide-title') {
            const title = this.extractTextFromNode(element);
            if (title) lines.push(`TITLE: ${title}`);
          }
        }
      }

      lines.push('');

      // Extract content
      if (slide.content) {
        for (const element of slide.content) {
          if (element.attrs?.className !== 'slide-title') {
            const text = this.extractTextFromNode(element);
            if (text) lines.push(text);
          }
        }
      }

      // Speaker notes
      if (slide.attrs?.semantic?.notes) {
        lines.push('');
        lines.push('NOTES:');
        lines.push(slide.attrs.semantic.notes);
      }

      lines.push('');
      lines.push('');
    }

    return lines.join('\n');
  }

  /**
   * Helper: Extract text from node
   */
  private extractTextFromNode(node: SerializedNode): string {
    if (node.text) return node.text;
    if (node.content) {
      return node.content.map(child => this.extractTextFromNode(child)).join(' ');
    }
    return '';
  }

  // ========================================================================
  // ELEMENT MANAGEMENT API
  // ========================================================================

  /**
   * Add element to current slide
   * @param params Element creation parameters
   * @param targetSlideNumber Optional slide number override (1-indexed). If not provided, uses currentSlideIndex.
   */
  public addElement(params: CreateElementParams, targetSlideNumber?: number): string | null {
    if (!this.yxml) {
      console.error('[PresentationsApplication] Cannot add element: yxml not initialized');
      return null;
    }

    // ✅ FIX (Bug #5): Use targetSlideNumber if provided, otherwise use currentSlideIndex
    const slideNumber = targetSlideNumber ?? (this.currentSlideIndex + 1);
    
    console.log('[PresentationsApplication] addElement called:', {
      currentSlideIndex: this.currentSlideIndex,
      targetSlideNumber,
      effectiveSlideNumber: slideNumber,
      totalSlides: this.getAllSlides().length,
      params
    });

    const slideElement = this.findSlideElement(slideNumber);
    if (!slideElement) {
      console.error('[PresentationsApplication] Cannot find slide element:', {
        currentSlideIndex: this.currentSlideIndex,
        targetSlideNumber,
        slideNumber,
        availableSlides: this.getAllSlides().map(s => s.attrs?.semantic?.slideNumber || 'unknown')
      });
      return null;
    }

    const elementId = this.generateId();
    const element = new Y.XmlElement(params.type);
    
    element.setAttribute('id', elementId);
    element.setAttribute('className', params.type);
    element.setAttribute('position', JSON.stringify(params.position));
    
    if (params.type === 'slide-text' || params.type === 'slide-title' || params.type === 'slide-subtitle' || 
        params.type === 'slide-bullet-list' || params.type === 'slide-numbered-list') {
      
      const theme = this.getCurrentTheme();
      const themeStyle: any = {
        fontFamily: DEFAULT_FONT_FAMILY,
        fontSize: DEFAULT_FONT_SIZE,
        lineHeight: DEFAULT_LINE_HEIGHT
      };
      
      if (theme) {
        themeStyle.fontFamily = theme.defaults.fontFamily;
        themeStyle.lineHeight = DEFAULT_LINE_HEIGHT;
        
        // Apply theme color based on element type
        if (params.type === 'slide-title') {
          themeStyle.color = theme.defaults.textColors.title;
          themeStyle.fontSize = theme.defaults.fontSizes.title;
        } else if (params.type === 'slide-subtitle') {
          themeStyle.color = theme.defaults.textColors.subtitle;
          themeStyle.fontSize = theme.defaults.fontSizes.subtitle;
        } else {
          themeStyle.color = theme.defaults.textColors.body;
          themeStyle.fontSize = theme.defaults.fontSizes.body;
        }
      }
      
      // User-provided style overrides theme
      const finalStyle = { ...themeStyle, ...params.style };
      element.setAttribute('style', JSON.stringify(finalStyle));
      
    } else if (params.style) {
      element.setAttribute('style', JSON.stringify(params.style));
    }

    // Handle text content
    if (params.type === 'slide-text' || params.type === 'slide-title' || params.type === 'slide-subtitle' ||
        params.type === 'slide-bullet-list' || params.type === 'slide-numbered-list') {
      if (!this.ydoc) {
        console.error('[PresentationsApplication] Cannot create text fragment: ydoc not initialized');
        return null;
      }

      this.ydoc.transact(() => {
        const fragmentsMap = this.ydoc!.getMap('textFragments');
        const fragmentKey = `fragment_${elementId}`;
        const ytext = new Y.Text();
        if (params.text) ytext.insert(0, params.text);
        fragmentsMap.set(fragmentKey, ytext);
        element.setAttribute('fragmentKey', fragmentKey);
      });
    }

    // Handle image
    if (params.type === 'slide-image' && params.src) {
      element.setAttribute('semantic', JSON.stringify({ 
        src: params.src, 
        alt: params.text || '',  // Use text param as alt text
        fileName: params.fileName || 'image'
      }));
    }

    // Handle shape
    if (params.type === 'slide-shape' && params.shapeType) {
      const semantic: any = { shapeType: params.shapeType };
      if (params.path) {
        semantic.path = params.path; // Store custom SVG path
      }
      // Store line/arrow endpoints if provided
      if ((params.shapeType === 'line' || params.shapeType === 'arrow') && 
          typeof params.x1 === 'number' && typeof params.y1 === 'number' &&
          typeof params.x2 === 'number' && typeof params.y2 === 'number') {
        // Store endpoints relative to element position
        semantic.x1 = params.x1 - params.position.x;
        semantic.y1 = params.y1 - params.position.y;
        semantic.x2 = params.x2 - params.position.x;
        semantic.y2 = params.y2 - params.position.y;
      }
      element.setAttribute('semantic', JSON.stringify(semantic));
    }

    // ✅ FIX: Wrap insertion in transaction with clientID for undo tracking
    if (!this.ydoc) return null;
    
    this.ydoc.transact(() => {
      slideElement.insert(slideElement.length, [element]);
    }, this.ydoc.clientID);

    return elementId;
  }

  /**
   * Update element position
   */
  public updateElementPosition(
    elementId: string,
    position: Partial<ElementPosition>
  ): boolean {
    const element = this.findElementById(elementId);
    if (!element || !this.ydoc) {
      console.error('[PresentationsApplication] Element not found:', elementId);
      return false;
    }

    const currentPosStr = element.getAttribute('position');
    if (!currentPosStr) return false;

    try {
      const currentPos = JSON.parse(currentPosStr);
      const newPos = { ...currentPos, ...position };
      
      // ✅ FIX: Wrap in transaction with clientID for undo tracking
      this.ydoc.transact(() => {
        element.setAttribute('position', JSON.stringify(newPos));
      }, this.ydoc.clientID);
      
      return true;
    } catch (error) {
      console.error('[PresentationsApplication] Failed to update position:', error);
      return false;
    }
  }

  /**
   * Update element style
   */
  public updateElementStyle(
    elementId: string,
    style: Record<string, any>
  ): boolean {
    const element = this.findElementById(elementId);
    if (!element || !this.ydoc) return false;

    const currentStyleStr = element.getAttribute('style') || '{}';
    try {
      const currentStyle = JSON.parse(currentStyleStr);
      const newStyle = { ...currentStyle, ...style };
      
      // ✅ FIX: Wrap in transaction with clientID for undo tracking
      this.ydoc.transact(() => {
        element.setAttribute('style', JSON.stringify(newStyle));
      }, this.ydoc.clientID);
      
      return true;
    } catch (error) {
      console.error('[PresentationsApplication] Failed to update style:', error);
      return false;
    }
  }

  /**
   * Update element text content
   * Handles both Y.Text (new contentEditable) and Y.XmlFragment (legacy ProseMirror)
   */
  public updateElementText(
    elementId: string,
    text: string,
    formatting?: FormattingRange[]
  ): boolean {
    const element = this.findElementById(elementId);
    if (!element || !this.ydoc) return false;

    const fragmentKey = element.getAttribute('fragmentKey');
    if (!fragmentKey) return false;

    const fragmentsMap = this.ydoc.getMap('textFragments');
    const fragment = fragmentsMap.get(fragmentKey);

    // Handle Y.Text (new contentEditable editor)
    if (fragment instanceof Y.Text) {
      this.ydoc.transact(() => {
        if (fragment.length > 0) fragment.delete(0, fragment.length);
        if (text.length > 0) fragment.insert(0, text);
        if (formatting && text.length > 0) {
          formatting.forEach(format => {
            if (format.start >= 0 && format.end <= text.length && format.start < format.end) {
              const marks: Record<string, boolean | string> = {};
              if (format.marks.strong) marks['bold'] = true;
              if (format.marks.em) marks['italic'] = true;
              if (format.marks.underline) marks['underline'] = true;
              if (Object.keys(marks).length > 0) {
                fragment.format(format.start, format.end - format.start, marks);
              }
            }
          });
        }
      }, this.ydoc.clientID);
      return true;
    }

    // Handle Y.XmlFragment (legacy ProseMirror — kept for backward compatibility)
    if (fragment instanceof Y.XmlFragment) {
      this.ydoc.transact(() => {
        let paragraph: Y.XmlElement;
        if (fragment.length > 0) {
          const firstChild = fragment.get(0);
          if (firstChild instanceof Y.XmlElement) {
            paragraph = firstChild;
          } else {
            paragraph = new Y.XmlElement('paragraph');
            paragraph.setAttribute('id', this.generateId());
            fragment.insert(0, [paragraph]);
          }
        } else {
          paragraph = new Y.XmlElement('paragraph');
          paragraph.setAttribute('id', this.generateId());
          fragment.insert(0, [paragraph]);
        }

        let textNode: Y.XmlText;
        if (paragraph.length > 0) {
          const firstChild = paragraph.get(0);
          if (firstChild instanceof Y.XmlText) {
            textNode = firstChild;
            textNode.delete(0, textNode.length);
          } else {
            textNode = new Y.XmlText();
            paragraph.insert(0, [textNode]);
          }
        } else {
          textNode = new Y.XmlText();
          paragraph.insert(0, [textNode]);
        }

        textNode.insert(0, text);

        if (formatting) {
          formatting.forEach(format => {
            if (format.start >= 0 && format.end <= text.length && format.start < format.end) {
              textNode.format(format.start, format.end - format.start, format.marks);
            }
          });
        }
      }, this.ydoc.clientID);
      return true;
    }

    return false;
  }

  /**
   * Delete element
   * DEFENSIVE FIX: Proper deletion order to prevent matchesNode errors
   * BUG FIX #1: Clean up setTimeout to prevent memory leaks
   * BUG FIX #6: Track fragment deletion with same transaction for undo/redo
   */
  public deleteElement(elementId: string): boolean {
    const slide = this.findSlideContainingElement(elementId);
    if (!slide || !this.ydoc) return false;

    for (let i = 0; i < slide.length; i++) {
      const child = slide.get(i);
      if (child instanceof Y.XmlElement && child.getAttribute('id') === elementId) {
        const fragmentKey = child.getAttribute('fragmentKey');
        
        console.log('[PresentationsApplication] Deleting element:', {
          elementId,
          hasFragment: !!fragmentKey,
          elementType: child.getAttribute('className')
        });
        
        // CRITICAL FIX: Delete in the correct order to prevent race conditions
        // 1. First delete the element from the slide (triggers React unmount)
        // 2. Then delete the Y.js fragment (after ProseMirror editors are destroyed)
        this.ydoc.transact(() => {
          // Step 1: Remove element from slide
          // This triggers React to unmount <TextElement> and destroy ProseMirror editor
          slide.delete(i, 1);
          
          // Step 2: Use setTimeout to ensure React cleanup completes before fragment deletion
          // This allows editor.destroy() to run before Y.js observer callbacks fire
          // BUG FIX #1: Store timeout ID for cleanup
          if (fragmentKey) {
            const timeoutId = setTimeout(() => {
              if (!this.ydoc) {
                console.warn('[PresentationsApplication] ydoc destroyed before fragment cleanup');
                return;
              }
              
              // BUG FIX #6: Use same clientID for undo/redo tracking
              this.ydoc.transact(() => {
            const fragmentsMap = this.ydoc!.getMap('textFragments');
                if (fragmentsMap.has(fragmentKey)) {
            fragmentsMap.delete(fragmentKey);
                  console.log('[PresentationsApplication] Cleaned up text fragment:', fragmentKey);
                } else {
                  console.warn('[PresentationsApplication] Fragment already deleted:', fragmentKey);
                }
              }, this.ydoc.clientID);
            }, 0);

            // Store timeout for potential cleanup (though unlikely to be needed in practice)
            // Store timeout ID for debugging if needed
            // @ts-ignore - Add to window for debugging if needed
            if (typeof window !== 'undefined') {
              (window as any)._lastFragmentCleanupTimeout = timeoutId;
            }
          }
        }, this.ydoc.clientID);
        
        console.log('[PresentationsApplication] Element deletion initiated:', elementId);
        return true;
      }
    }

    console.warn('[PresentationsApplication] Element not found for deletion:', elementId);
    return false;
  }

  /**
   * Get all elements for a specific slide by slide number (1-indexed)
   * @param slideNumber - Slide number (1, 2, 3, ...)
   * @returns Array of slide elements
   */
  public getSlideElements(slideNumber: number): SlideElement[] {
    if (!this.yxml || !this.ydoc) return [];

    let slideElement: Y.XmlElement | null = null;
    
    for (let i = 0; i < this.yxml.length; i++) {
      const element = this.yxml.get(i);
      if (!(element instanceof Y.XmlElement)) continue;
      
      const semanticStr = element.getAttribute('semantic');
      if (!semanticStr) continue;
      
      try {
        const semantic = JSON.parse(semanticStr);
        if (semantic.slideNumber === slideNumber) {
          slideElement = element;
          break;
        }
      } catch {
        continue;
      }
    }
    
    if (!slideElement) return [];

    const elements: SlideElement[] = [];

    for (let i = 0; i < slideElement.length; i++) {
      const child = slideElement.get(i);
      
      if (!(child instanceof Y.XmlElement)) continue;
      
      const id = child.getAttribute('id');
      const type = child.getAttribute('className') as SlideElement['type'];
      const positionStr = child.getAttribute('position');
      
      if (!id || !type || !positionStr) continue;

      try {
        const position = typeof positionStr === 'string' ? JSON.parse(positionStr) : positionStr;
        const styleStr = child.getAttribute('style');
        const style = styleStr ? (typeof styleStr === 'string' ? JSON.parse(styleStr) : styleStr) : undefined;

        const baseElement = { id, type, position, style, yElement: child };

        if (type === 'slide-text' || type === 'slide-title' || type === 'slide-subtitle' ||
            type === 'slide-bullet-list' || type === 'slide-numbered-list') {
          
          const fragmentKey = child.getAttribute('fragmentKey');
          
          if (fragmentKey) {
            const fragmentsMap = this.ydoc.getMap('textFragments');
            const fragment = fragmentsMap.get(fragmentKey);
            
            if (fragment instanceof Y.Text) {
              elements.push({ ...baseElement, content: fragment } as SlideElement);
              continue;
            } else {
              console.warn('[PresentationsApplication] Text element missing Y.Text:', { id, fragmentKey });
            }
          } else {
            console.warn('[PresentationsApplication] Text element missing fragmentKey:', id);
          }
        }

        // For shape elements, extract shapeType and path from semantic
        if (type === 'slide-shape') {
          const semanticStr = child.getAttribute('semantic');
          try {
            const semantic = semanticStr ? JSON.parse(semanticStr) : {};
            elements.push({
              ...baseElement,
              shapeType: semantic.shapeType || 'rectangle',
              path: semantic.path,
              x1: semantic.x1,
              y1: semantic.y1,
              x2: semantic.x2,
              y2: semantic.y2,
            } as SlideElement);
          } catch {
            elements.push({ ...baseElement, shapeType: 'rectangle' } as SlideElement);
          }
          continue;
        }

        // For image elements, extract src/alt/fileName from semantic
        if (type === 'slide-image') {
          const semanticStr = child.getAttribute('semantic');
          try {
            const semantic = semanticStr ? JSON.parse(semanticStr) : {};
            elements.push({
              ...baseElement,
              src: semantic.src || '',
              alt: semantic.alt,
              fileName: semantic.fileName,
            } as SlideElement);
          } catch {
            elements.push(baseElement);
          }
          continue;
        }

        elements.push(baseElement);
      } catch (error) {
        console.error('[PresentationsApplication] Failed to parse element:', { id, error });
      }
    }

    return elements;
  }

  /**
   * Get all elements on current slide
   * Convenience wrapper around getSlideElements()
   */
  public getCurrentSlideElements(): SlideElement[] {
    return this.getSlideElements(this.currentSlideIndex + 1);
  }

  /**
   * Get Y.js element by ID
   */
  public getYElementById(elementId: string): Y.XmlElement | null {
    return this.findElementById(elementId);
  }

  // ========================================================================
  // IMAGE-SPECIFIC METHODS
  // ========================================================================

  /**
   * Add image to current slide
   * Convenience method for adding images with proper validation
   */
  public addImage(params: {
    x: number;
    y: number;
    width: number;
    height: number;
    src: string;
    alt?: string;
    fileName?: string;
  }): string | null {
    return this.addElement({
      type: 'slide-image',
      position: {
        type: 'absolute',
        x: params.x,
        y: params.y,
        width: params.width,
        height: params.height
      },
      src: params.src,
      text: params.alt,      // Use text field for alt text
      fileName: params.fileName
    });
  }

  /**
   * Update image source URL
   * Useful for AI to change image content
   */
  public updateImageSrc(elementId: string, newSrc: string, alt?: string): boolean {
    const element = this.findElementById(elementId);
    if (!element || !this.ydoc) {
      console.error('[PresentationsApplication] Element not found:', elementId);
      return false;
    }

    const semanticStr = element.getAttribute('semantic');
    if (!semanticStr) {
      console.error('[PresentationsApplication] Element has no semantic data');
      return false;
    }

    try {
      const semantic = JSON.parse(semanticStr);
      semantic.src = newSrc;
      if (alt !== undefined) {
        semantic.alt = alt;
      }
      
      // ✅ FIX: Wrap in transaction with clientID for undo tracking
      this.ydoc.transact(() => {
        element.setAttribute('semantic', JSON.stringify(semantic));
      }, this.ydoc.clientID);
      
      return true;
    } catch (error) {
      console.error('[PresentationsApplication] Failed to update image src:', error);
      return false;
    }
  }

  /**
   * Get image element data
   */
  public getImageElement(elementId: string): { src: string; alt?: string; fileName?: string } | null {
    const element = this.findElementById(elementId);
    if (!element) return null;

    const semanticStr = element.getAttribute('semantic');
    if (!semanticStr) return null;

    try {
      const semantic = JSON.parse(semanticStr);
      return {
        src: semantic.src,
        alt: semantic.alt,
        fileName: semantic.fileName
      };
    } catch (error) {
      console.error('[PresentationsApplication] Failed to get image data:', error);
      return null;
    }
  }

  // ========================================================================
  // HELPER METHODS
  // ========================================================================

  /**
   * Find slide element in Y.js by slide number
   * Made public for PDF export functionality
   */
  public findSlideElement(slideNumber: number): Y.XmlElement | null {
    if (!this.yxml) return null;

    console.log('[PresentationsApplication] 🔍 findSlideElement (Legacy method - use getSlideElements instead):', {
      searchingFor: slideNumber,
      totalSlides: this.yxml.length,
      ydocGuid: this.ydoc?.guid,
      yxmlDocGuid: this.yxml.doc?.guid,
      guidMatch: this.ydoc?.guid === this.yxml.doc?.guid
    });

    for (let i = 0; i < this.yxml.length; i++) {
      const element = this.yxml.get(i);
      if (element instanceof Y.XmlElement) {
        const semanticStr = element.getAttribute('semantic');
        if (semanticStr) {
          try {
            const semantic = JSON.parse(semanticStr);
            const slideId = element.getAttribute('id');
            
            console.log('[PresentationsApplication] 📄 Checking slide:', {
              index: i,
              slideNumber: semantic.slideNumber,
              slideId,
              childCount: element.length,
              matches: semantic.slideNumber === slideNumber
            });
            
            if (semantic.slideNumber === slideNumber) {
              console.log('[PresentationsApplication] ✅ Found slide:', {
                slideNumber,
                slideId,
                childCount: element.length
              });
              return element;
            }
          } catch (e) {
            continue;
          }
        }
      }
    }

    console.log('[PresentationsApplication] ❌ Slide not found:', slideNumber);
    return null;
  }

  /**
   * Find element by ID in Y.js
   */
  private findElementById(elementId: string): Y.XmlElement | null {
    if (!this.yxml) return null;

    // Search through all slides
    for (let i = 0; i < this.yxml.length; i++) {
      const slide = this.yxml.get(i);
      if (!(slide instanceof Y.XmlElement)) continue;

      // Search children
      for (let j = 0; j < slide.length; j++) {
        const child = slide.get(j);
        if (child instanceof Y.XmlElement && child.getAttribute('id') === elementId) {
          return child;
        }
      }
    }

    return null;
  }

  /**
   * Find slide containing element
   */
  private findSlideContainingElement(elementId: string): Y.XmlElement | null {
    if (!this.yxml) return null;

    for (let i = 0; i < this.yxml.length; i++) {
      const slide = this.yxml.get(i);
      if (!(slide instanceof Y.XmlElement)) continue;

      for (let j = 0; j < slide.length; j++) {
        const child = slide.get(j);
        if (child instanceof Y.XmlElement && child.getAttribute('id') === elementId) {
          return slide;
        }
      }
    }

    return null;
  }

  /**
   * Convert SerializedNode to SlideElement
   */
  private nodeToSlideElement(node: SerializedNode): SlideElement | null {
    if (!node.attrs?.id || !node.attrs?.className || !node.attrs?.position) {
      return null;
    }

    const yElement = this.findElementById(node.attrs.id);
    if (!yElement || !this.ydoc) return null;

    const baseElement = {
      id: node.attrs.id,
      type: node.attrs.className as SlideElement['type'],
      position: typeof node.attrs.position === 'string' 
        ? JSON.parse(node.attrs.position) 
        : node.attrs.position,
      style: node.attrs.style 
        ? (typeof node.attrs.style === 'string' ? JSON.parse(node.attrs.style) : node.attrs.style)
        : undefined,
      yElement
    };

    // For text elements, get the Y.XmlFragment from the textFragments map
    if (baseElement.type === 'slide-text' || baseElement.type === 'slide-title' || baseElement.type === 'slide-subtitle') {
      const fragmentKey = yElement.getAttribute('fragmentKey');
      console.log('[PresentationsApplication] 🔍 nodeToSlideElement: Looking for Y.XmlFragment', {
        elementId: baseElement.id,
        fragmentKey,
        hasFragmentKey: !!fragmentKey
      });
      
      if (fragmentKey) {
        const fragmentsMap = this.ydoc.getMap('textFragments');
        const fragment = fragmentsMap.get(fragmentKey);
        
        console.log('[PresentationsApplication] 🔍 nodeToSlideElement: Retrieved from map', {
          fragmentKey,
          fragmentExists: !!fragment,
          fragmentType: fragment?.constructor?.name,
          fragmentLength: fragment instanceof Y.XmlFragment ? fragment.length : 'N/A'
        });
        
        if (fragment instanceof Y.XmlFragment) {
          console.log('[PresentationsApplication] ✅ nodeToSlideElement: Attaching Y.XmlFragment to element', {
            elementId: baseElement.id,
            contentLength: fragment.length
          });
          return {
            ...baseElement,
            content: fragment
          } as SlideElement;
        } else {
          console.warn('[PresentationsApplication] Text element missing Y.XmlFragment:', { id: baseElement.id, fragmentKey, fragmentType: fragment?.constructor?.name });
        }
      } else {
        console.warn('[PresentationsApplication] Text element missing fragmentKey attribute:', baseElement.id);
      }
    }

    // For shape elements, extract shapeType and path from semantic
    if (baseElement.type === 'slide-shape') {
      const semanticStr = yElement.getAttribute('semantic');
      if (semanticStr) {
        try {
          const semantic = JSON.parse(semanticStr);
          return {
            ...baseElement,
            shapeType: semantic.shapeType || 'rectangle',
            path: semantic.path
          } as SlideElement;
        } catch (e) {
          console.error('[PresentationsApplication] Failed to parse shape semantic:', e);
        }
      }
      // Fallback if no semantic attribute
      return {
        ...baseElement,
        shapeType: 'rectangle'
      } as SlideElement;
    }

    return baseElement;
  }

  /**
   * Get Y.Doc (public accessor)
   */
  public getYDoc(): Y.Doc | null {
    return this.ydoc || null;
  }

  /**
   * Get awareness (for collaborative cursors)
   */
  public getAwareness(): any {
    return this.collaborationProvider?.awareness || null;
  }

  // ========================================================================
  // AI ENTITY_WRITE SUPPORT - Helper Methods
  // ========================================================================

  /**
   * Override serializeNode to add presentation-specific context
   * Base class now handles JSON parsing, we just add parent slide info
   */
  protected serializeNode(element: Y.XmlElement): any {
    // Get base serialization with parsed attrs
    const node = super.serializeNode(element);
    
    // Add parent slide context for elements (not for slides themselves)
    if (element.parent && element.parent instanceof Y.XmlElement) {
      const parentClassName = element.parent.getAttribute('className');
      
      if (parentClassName === 'slide') {
        const parentId = element.parent.getAttribute('id');
        const parentSemantic = element.parent.getAttribute('semantic');
        
        if (parentId) {
          node.parentSlideId = parentId;
          
          // Extract slide number from parent's semantic
          if (parentSemantic) {
            try {
              const semantic = typeof parentSemantic === 'string' 
                ? JSON.parse(parentSemantic) 
                : parentSemantic;
              
              if (semantic.slideNumber !== undefined) {
                node.slideNumber = semantic.slideNumber;
              }
            } catch (e) {
              console.warn('[PresentationsApplication] Failed to parse parent semantic:', e);
            }
          }
        }
      }
    }
    
    return node;
  }

  /**
   * Helper: Resolve target slide number from various target formats
   * ✅ Validates that slide exists
   */
  private resolveTargetSlide(target: any): number {
    // ✅ FIX (Bug #3): More robust containerId handling
    if (target?.containerId) {
      // Search Y.js directly for the container ID (don't use byIdIndex which has ProseMirror nodes)
      if (this.yxml) {
        // Check if containerId is a slide
        for (let i = 0; i < this.yxml.length; i++) {
          const slide = this.yxml.get(i) as Y.XmlElement;
          if (slide && slide.getAttribute && slide.getAttribute('id') === target.containerId) {
            // This IS a slide
            const semanticStr = slide.getAttribute('semantic');
            if (semanticStr) {
              try {
                const semantic = JSON.parse(semanticStr);
                return semantic.slideNumber || 1;
              } catch (e) {
                console.warn('[PresentationsApplication] Failed to parse semantic:', e);
              }
            }
            return i + 1; // Fallback to position-based
          }
          
          // Check if this slide contains an element with containerId
          if (slide && slide.getAttribute && slide.getAttribute('className') === 'slide') {
            for (let j = 0; j < slide.length; j++) {
              const child = slide.get(j);
              if (child instanceof Y.XmlElement && child.getAttribute('id') === target.containerId) {
                // Found the element, return this slide's number
                const semanticStr = slide.getAttribute('semantic');
                if (semanticStr) {
                  try {
                    const semantic = JSON.parse(semanticStr);
                    return semantic.slideNumber || 1;
                  } catch (e) {
                    console.warn('[PresentationsApplication] Failed to parse semantic:', e);
                  }
                }
                return i + 1; // Fallback to position-based number
              }
            }
          }
        }
      }
      
      // Couldn't find container, throw error instead of silent fallback
      throw new Error(`Container not found: ${target.containerId}`);
    }
    
    // ✅ CRITICAL FIX: Validate slideNumber exists
    if (target?.slideNumber) {
      const slides = this.getAllSlides();
      if (target.slideNumber < 1 || target.slideNumber > slides.length) {
        throw new Error(`Slide ${target.slideNumber} does not exist (valid range: 1-${slides.length})`);
      }
      return target.slideNumber;
    }
    
    return this.currentSlideIndex + 1;
  }

  /**
   * Helper: Parse position from string or object
   * ✅ Validates and clamps to canvas bounds (800x450)
   */
  private parsePosition(position: any, defaults: ElementPosition): ElementPosition {
    if (typeof position === 'string') {
      try {
        position = JSON.parse(position);
      } catch (e) {
        console.warn('[PresentationsApplication] Failed to parse position:', e);
        return defaults;
      }
    }
    
    const result = { ...defaults, ...position };
    
    // ✅ CRITICAL FIX: Validate and clamp to canvas bounds
    const CANVAS_WIDTH = 800;
    const CANVAS_HEIGHT = 450;
    
    // Clamp position to canvas
    if (result.x < 0) {
      console.warn(`[PresentationsApplication] x position ${result.x} out of bounds, clamping to 0`);
      result.x = 0;
    }
    if (result.y < 0) {
      console.warn(`[PresentationsApplication] y position ${result.y} out of bounds, clamping to 0`);
      result.y = 0;
    }
    
    // Ensure element stays within canvas (accounting for width/height)
    const maxX = CANVAS_WIDTH - (result.width || 0);
    const maxY = CANVAS_HEIGHT - (result.height || 0);
    
    if (result.x > maxX) {
      console.warn(`[PresentationsApplication] x position ${result.x} exceeds canvas bounds, clamping to ${maxX}`);
      result.x = Math.max(0, maxX);
    }
    if (result.y > maxY) {
      console.warn(`[PresentationsApplication] y position ${result.y} exceeds canvas bounds, clamping to ${maxY}`);
      result.y = Math.max(0, maxY);
    }
    
    // Validate dimensions
    if (result.width && result.width > CANVAS_WIDTH) {
      console.warn(`[PresentationsApplication] width ${result.width} exceeds canvas, clamping to ${CANVAS_WIDTH}`);
      result.width = CANVAS_WIDTH;
    }
    if (result.height && result.height > CANVAS_HEIGHT) {
      console.warn(`[PresentationsApplication] height ${result.height} exceeds canvas, clamping to ${CANVAS_HEIGHT}`);
      result.height = CANVAS_HEIGHT;
    }
    
    return result;
  }

  // ========================================================================
  // AI ENTITY_WRITE SUPPORT - Main Handler Override
  // ========================================================================

  /**
   * Override handleEntityWrite to support presentation-specific operations
   */
  async handleEntityWrite(params: any): Promise<any> {
    console.log('[PresentationsApplication] handleEntityWrite (presentations-specific):', params);
    
    let { operation, target, content, attrs, operations } = params;
    
    // ✅ CRITICAL FIX: Parse JSON strings before processing
    // Content might arrive as JSON string from AI
    if (typeof content === 'string') {
      try {
        const parsed = JSON.parse(content);
        content = parsed;
        console.log('[PresentationsApplication] ✅ Parsed JSON string content:', {
          type: Array.isArray(parsed) ? 'array' : 'object',
          className: Array.isArray(parsed) ? parsed[0]?.className : parsed.className
        });
      } catch (e) {
        // Not JSON - might be plain text, let handlers deal with it
        console.log('[PresentationsApplication] Content is not JSON, passing as-is');
      }
    }
    
    // Route to presentation-specific handlers
    switch (operation) {
      case 'insert':
        return this.handlePresentationInsert(target, content, attrs);
      
      case 'update':
        return this.handlePresentationUpdate(target, content, attrs);
      
      case 'replace':
        // ✅ CONSISTENCY FIX: Handle replace as update for presentations
        // This prevents BaseApplication from parsing markdown and splitting spatial elements
        console.log('[PresentationsApplication] Replace operation → routing to update handler');
        return this.handlePresentationUpdate(target, content, attrs);
      
      case 'delete':
        return this.handlePresentationDelete(target);
      
      case 'batch':
        // ✅ CRITICAL FIX: Handle batch operations with presentation-specific logic
        return this.handlePresentationBatch(operations);
      
      default:
        // ✅ CONSISTENCY FIX: Don't fall back to BaseApplication to avoid markdown parsing
        throw new Error(`[PresentationsApplication] Unsupported operation: ${operation}. Supported: insert, update, replace, delete, batch`);
    }
  }

  /**
   * Handle batch operations for presentations
   * ✅ Ensures all operations go through presentation-specific validation
   * ✅ RESILIENT: Continues execution even if individual operations fail
   */
  private async handlePresentationBatch(operations: any[]): Promise<any> {
    console.log('[PresentationsApplication] 🔧 Handling batch operation:', {
      operationCount: operations.length
    });
    
    if (!operations || operations.length === 0) {
      throw new Error('Batch operation requires operations array');
    }
    
    // ✅ Enforce batch size limit
    if (operations.length > 100) {
      throw new Error(`Batch limited to 100 operations (got ${operations.length})`);
    }
    
    const results: any[] = [];
    const affectedNodes: string[] = [];
    const succeededOperations: number[] = [];
    const failedOperations: number[] = [];
    
    // Execute each operation through presentation-specific handlers
    for (let i = 0; i < operations.length; i++) {
      const op = operations[i];
        const operation = op.operation || op.op;
      
      console.log(`[PresentationsApplication] 🔄 Executing operation ${i + 1}/${operations.length}:`, {
        operationIndex: i,
        operationType: operation,
        elementType: op.content?.className
      });
      
      try {
        let result;
        
        // ✅ CRITICAL FIX: Parse JSON string content in batch operations
        let content = op.content;
        if (typeof content === 'string') {
          try {
            content = JSON.parse(content);
            console.log('[PresentationsApplication] ✅ Parsed JSON in batch operation');
          } catch (e) {
            // Not JSON, use as-is
          }
        }
        
        switch (operation) {
          case 'insert':
            result = await this.handlePresentationInsert(op.target, content, op.attrs);
            break;
          case 'update':
            result = await this.handlePresentationUpdate(op.target, content, op.attrs);
            break;
          case 'delete':
            result = await this.handlePresentationDelete(op.target);
            break;
          default:
            throw new Error(`Unknown operation in batch: ${operation}`);
        }
        
        // ✅ SUCCESS: Track successful operation
        succeededOperations.push(i);
        results.push(result);
        if (result.elementId) affectedNodes.push(result.elementId);
        if (result.slideId) affectedNodes.push(result.slideId);
        
        console.log(`[PresentationsApplication] ✅ Operation ${i + 1} succeeded:`, {
          operationIndex: i,
          elementId: result.elementId,
          slideId: result.slideId
        });
        
      } catch (error: any) {
        // ✅ RESILIENT: Log error but CONTINUE executing remaining operations
        failedOperations.push(i);
        
        const errorResult = {
          success: false,
          operationIndex: i,
          operationType: operation,
          elementType: op.content?.className || 'unknown',
          error: error.message,
          stack: error.stack
        };
        
        results.push(errorResult);
        
        console.error(`[PresentationsApplication] ❌ Operation ${i + 1} failed:`, {
          operationIndex: i,
          operationType: operation,
          elementType: op.content?.className,
          error: error.message
        });
      }
    }
    
    // ✅ Rebuild indices if at least ONE operation succeeded
    if (succeededOperations.length > 0) {
      console.log('[PresentationsApplication] 🔄 Rebuilding indices after partial/full success');
      await this.buildCoreIndices();
    }
    
    // ✅ Generate contextual message
    const total = operations.length;
    const succeeded = succeededOperations.length;
    const failed = failedOperations.length;
    
    let message: string;
    if (failed === 0) {
      message = `Batch: All ${total} operations completed successfully`;
    } else if (succeeded === 0) {
      message = `Batch: All ${total} operations failed`;
    } else {
      message = `Batch: ${succeeded} of ${total} operations succeeded (${failed} failed)`;
    }
    
    console.log('[PresentationsApplication] ✅ Batch complete:', {
      total,
      succeeded,
      failed,
      partialSuccess: succeeded > 0 && failed > 0
    });
    
    // ✅ NEW: Emit event to force React sync after batch completes
    // This ensures all newly created elements render immediately
    if (succeeded > 0) {
      console.log('[PresentationsApplication] 📢 Emitting batchComplete event to force sync');
      this.emit('batchComplete', { 
        operationCount: total,
        succeededCount: succeeded,
        failedCount: failed
      });
    }
    
    return {
      success: succeeded > 0, // Overall success if at least one operation succeeded
      message,
      affectedNodes,
      results,
      stats: {
        total,
        succeeded,
        failed,
        partialSuccess: succeeded > 0 && failed > 0,
        succeededOperations,
        failedOperations
      }
    };
  }

  /**
   * Handle presentation-specific insert operations
   */
  private async handlePresentationInsert(target: any, content: any, attrs: any): Promise<any> {
    const className = content?.className || content?.nodeName;
    
    console.log('[PresentationsApplication] Insert operation:', {
      className,
      target,
      hasAttrs: !!attrs
    });
    
    switch (className) {
      case 'slide':
        return await this.insertSlide(target, content, attrs);
      
      case 'slide-text':
      case 'slide-title':
      case 'slide-subtitle':
      case 'slide-bullet-list':
      case 'slide-numbered-list':
        return await this.insertTextElement(target, content, attrs);
      
      case 'slide-shape':
        return await this.insertShapeElement(target, content, attrs);
      
      case 'slide-image':
        return await this.insertImageElement(target, content, attrs);
      
      default:
        throw new Error(`Unknown element type: ${className}. Must be: slide, slide-text, slide-title, slide-subtitle, slide-bullet-list, slide-numbered-list, slide-shape, or slide-image`);
    }
  }

  /**
   * Insert a new slide
   */
  private async insertSlide(target: any, content: any, attrs: any): Promise<any> {
    if (!this.yxml || !this.ydoc) {
      throw new Error('Y.js not initialized');
    }

    // Determine slide number
    let slideNumber: number;
    const slides = this.getAllSlides();
    
    if (attrs?.semantic?.slideNumber) {
      slideNumber = attrs.semantic.slideNumber;
    } else if (target?.at === 'end') {
      slideNumber = slides.length + 1;
    } else if (target?.after) {
      const afterSlide = this.byIdIndex.get(target.after);
      const afterNum = afterSlide?.attrs?.semantic?.slideNumber || 0;
      slideNumber = afterNum + 1;
      // Note: Slide renumbering would be handled here in a full implementation
    } else if (target?.before) {
      const beforeSlide = this.byIdIndex.get(target.before);
      const beforeNum = beforeSlide?.attrs?.semantic?.slideNumber || 1;
      slideNumber = beforeNum;
      // Note: Slide renumbering would be handled here in a full implementation
    } else {
      slideNumber = slides.length + 1;
    }
    
    // Create slide element
    const slideId = this.generateId();
    const slide = new Y.XmlElement('slide');
    slide.setAttribute('id', slideId);
    slide.setAttribute('className', 'slide');
    
    // ✅ Apply theme defaults
    const theme = this.getCurrentTheme();
    const defaultBackground = theme?.defaults.slideBackground || '#ffffff';
    
    const semantic = {
      slideNumber,
      layout: attrs?.semantic?.layout || content?.attrs?.semantic?.layout || 'content',
      transition: attrs?.semantic?.transition || content?.attrs?.semantic?.transition || 'slide',
      backgroundColor: attrs?.semantic?.backgroundColor || content?.attrs?.semantic?.backgroundColor || defaultBackground,
      ...(attrs?.semantic || {})
    };
    
    slide.setAttribute('semantic', JSON.stringify(semantic));
    
    // Insert slide at appropriate position
    this.ydoc.transact(() => {
      this.yxml!.insert(slideNumber - 1, [slide]);
      
      // ✅ FIX (Bug #1): Renumber subsequent slides when inserting in middle
      if (target?.after || target?.before) {
        // Renumber all slides after the insertion point
        for (let i = slideNumber; i < this.yxml!.length; i++) {
          const existingSlide = this.yxml!.get(i) as Y.XmlElement;
          if (existingSlide && existingSlide.getAttribute('className') === 'slide') {
            const existingSemanticStr = existingSlide.getAttribute('semantic') || '{}';
            try {
              const existingSemantic = JSON.parse(existingSemanticStr);
              existingSemantic.slideNumber = i + 1; // New slide number based on position
              existingSlide.setAttribute('semantic', JSON.stringify(existingSemantic));
            } catch (e) {
              console.warn('[PresentationsApplication] Failed to parse semantic for renumbering:', e);
            }
          }
        }
        console.log('[PresentationsApplication] ✅ Renumbered subsequent slides');
      }
    }, this.ydoc.clientID);
    
    // ✅ FIX (Bug #2): Rebuild indices so new slide is discoverable
    await this.buildCoreIndices();
    
    console.log('[PresentationsApplication] ✅ Created slide:', {
      slideId,
      slideNumber,
      layout: semantic.layout
    });
    
    return {
      success: true,
      slideId,
      slideNumber,
      message: `Created slide ${slideNumber}`
    };
  }

  /**
   * Insert a text element
   */
  private async insertTextElement(target: any, content: any, attrs: any): Promise<any> {
    // Find target slide
    const slideNumber = this.resolveTargetSlide(target);
    
    // Parse position
    const position = this.parsePosition(
      content.attrs?.position,
      { type: 'absolute', x: 100, y: 100, width: 200, height: 100, z: 1 }
    );
    
    // Get text content
    const text = content.attrs?.text || content.text || 'New text';
    const formatting = content.attrs?.formatting || content.formatting || [];
    const elementType = content.className || 'slide-text';
    
    // ✅ THEME FIX: Apply theme defaults merged with user style
    const userStyle = content.attrs?.style || {};
    const themedAttrs = this.getDefaultAttrsWithTheme(elementType, { style: userStyle });
    
    // ✅ FIX (Bug #5): Pass slideNumber directly, don't modify currentSlideIndex
    const elementId = this.addElement({
      type: elementType as SlideElement['type'],
      position,
      text,
      formatting,
      style: themedAttrs.style // Use theme-aware style
    }, slideNumber);
    
    if (!elementId) {
      throw new Error('Failed to create text element');
    }
    
    // ✅ FIX (Bug #2): Rebuild indices so new element is discoverable
    await this.buildCoreIndices();
    
    console.log('[PresentationsApplication] ✅ Created text element:', {
      elementId,
      slideNumber,
      type: elementType,
      text: text.substring(0, 50)
    });
    
    return {
      success: true,
      elementId,
      slideNumber,
      message: `Created text element on slide ${slideNumber}`
    };
  }

  /**
   * Insert a shape element
   */
  private async insertShapeElement(target: any, content: any, attrs: any): Promise<any> {
    const slideNumber = this.resolveTargetSlide(target);
    
    // Parse position and style
    const position = this.parsePosition(
      content.attrs?.position,
      { type: 'absolute', x: 100, y: 100, width: 150, height: 150, z: 1 }
    );
    
    let style = content.attrs?.style || content.style || {};
    if (typeof style === 'string') {
      try {
        style = JSON.parse(style);
      } catch (e) {
        style = {};
      }
    }
    
    // ✅ THEME FIX: Apply theme defaults for shapes
    const themedAttrs = this.getDefaultAttrsWithTheme('slide-shape', { style });
    
    style = {
      opacity: 1,
      ...themedAttrs.style // Use theme-aware style (includes default fill)
    };
    
    // ✅ Extract shapeType from new pattern only
    const shapeType = content.attrs?.semantic?.shapeType || 'rectangle';
    
    // Validate shape type
    const validShapes = ['rectangle', 'circle', 'triangle', 'arrow', 'line', 'custom', 'diamond', 'roundedRect', 'ellipse', 'pentagon', 'hexagon', 'star', 'trapezoid', 'parallelogram', 'rightTriangle'];
    if (!validShapes.includes(shapeType)) {
      throw new Error(`Invalid shape type: ${shapeType}. Must be one of: ${validShapes.join(', ')}`);
    }
    
    // ✅ Extract path for custom shapes
    const path = content.attrs?.semantic?.path;
    
    // ✅ Validate custom shapes have path
    if (shapeType === 'custom' && !path) {
      throw new Error('Custom shapes (shapeType="custom") require semantic.path attribute with SVG path data');
    }
    
    // ✅ FIX (Bug #5): Pass slideNumber directly, don't modify currentSlideIndex
    const elementId = this.addElement({
      type: 'slide-shape',
      position,
      shapeType,
      style,
      path // ✅ FIX: Pass path to addElement for custom shapes
    }, slideNumber);
    
    if (!elementId) {
      throw new Error('Failed to create shape element');
    }
    
    // ✅ FIX (Bug #2): Rebuild indices so new element is discoverable
    await this.buildCoreIndices();
    
    console.log('[PresentationsApplication] ✅ Created shape element:', {
      elementId,
      slideNumber,
      shapeType
    });
    
    return {
      success: true,
      elementId,
      slideNumber,
      shapeType,
      message: `Created ${shapeType} shape on slide ${slideNumber}`
    };
  }

  /**
   * Insert an image element
   */
  private async insertImageElement(target: any, content: any, attrs: any): Promise<any> {
    const slideNumber = this.resolveTargetSlide(target);
    
    const position = this.parsePosition(
      content.attrs?.position,
      { type: 'absolute', x: 100, y: 100, width: 300, height: 200, z: 1 }
    );
    
    // ✅ Extract src from new pattern only
    const src = content.attrs?.semantic?.src || '';
    
    if (!src) {
      throw new Error('Image src/url is required');
    }
    
    // ✅ FIX (Bug #5): Pass slideNumber directly, don't modify currentSlideIndex
    const elementId = this.addElement({
      type: 'slide-image',
      position,
      src
    }, slideNumber);
    
    if (!elementId) {
      throw new Error('Failed to create image element');
    }
    
    // ✅ FIX (Bug #2): Rebuild indices so new element is discoverable
    await this.buildCoreIndices();
    
    console.log('[PresentationsApplication] ✅ Created image element:', {
      elementId,
      slideNumber,
      src: src.substring(0, 50)
    });
    
    return {
      success: true,
      elementId,
      slideNumber,
      message: `Created image on slide ${slideNumber}`
    };
  }

  /**
   * Handle presentation-specific update operations
   */
  private async handlePresentationUpdate(target: any, content: any, attrs: any): Promise<any> {
    const elementId = target?.nodeId || attrs?.id;
    
    if (!elementId) {
      throw new Error('nodeId or attrs.id required for update operation');
    }
    
    const element = this.findElementById(elementId);
    if (!element) {
      throw new Error(`Element not found: ${elementId}`);
    }
    
    const updates: string[] = [];
    
    // Update position
    if (attrs?.position) {
      const position = this.parsePosition(attrs.position, { type: 'absolute', x: 0, y: 0, width: 100, height: 100 });
      if (this.updateElementPosition(elementId, position)) {
        updates.push('position');
      }
    }
    
    // Update style
    if (attrs?.style) {
      let style = attrs.style;
      if (typeof style === 'string') {
        try {
          style = JSON.parse(style);
        } catch (e) {
          console.warn('[PresentationsApplication] Failed to parse style:', e);
        }
      }
      if (this.updateElementStyle(elementId, style)) {
        updates.push('style');
      }
    }
    
    // Update text
    if (attrs?.text !== undefined) {
      if (this.updateElementText(elementId, attrs.text, attrs?.formatting)) {
        updates.push('text');
      }
    }
    
    // Update slide attributes (layout, backgroundColor, etc.)
    if (attrs?.semantic && element.getAttribute('className') === 'slide') {
      if (!this.ydoc) {
        throw new Error('Y.js not initialized');
      }
      
      this.ydoc.transact(() => {
        const currentSemanticStr = element.getAttribute('semantic') || '{}';
        let currentSemantic = {};
        try {
          currentSemantic = JSON.parse(currentSemanticStr);
        } catch (e) {
          console.warn('[PresentationsApplication] Failed to parse current semantic:', e);
        }
        
        let newSemantic = attrs.semantic;
        if (typeof newSemantic === 'string') {
          try {
            newSemantic = JSON.parse(newSemantic);
          } catch (e) {
            console.warn('[PresentationsApplication] Failed to parse new semantic:', e);
          }
        }
        
        const mergedSemantic = { ...currentSemantic, ...newSemantic };
        element.setAttribute('semantic', JSON.stringify(mergedSemantic));
      }, this.ydoc.clientID);
      
      updates.push('semantic');
    }
    
    // ✅ FIX (Bug #2): Rebuild indices to reflect updates
    await this.buildCoreIndices();
    
    console.log('[PresentationsApplication] ✅ Updated element:', {
      elementId,
      updates
    });
    
    return {
      success: true,
      elementId,
      updates,
      message: `Updated ${updates.join(', ')} on element ${elementId}`
    };
  }

  /**
   * Handle presentation-specific delete operations
   */
  private async handlePresentationDelete(target: any): Promise<any> {
    if (target?.nodeId) {
      // ✅ FIX (Bug #6): Check if it's an element first, then check if it's a slide
      let element = this.findElementById(target.nodeId);
      let isSlide = false;
      
      if (!element && this.yxml) {
        // Maybe it's a slide ID - search slides directly
        for (let i = 0; i < this.yxml.length; i++) {
          const slide = this.yxml.get(i) as Y.XmlElement;
          if (slide && slide.getAttribute('id') === target.nodeId) {
            element = slide;
            isSlide = true;
            break;
          }
        }
      } else if (element) {
        // Found as element, check if it's a slide
        isSlide = element.getAttribute('className') === 'slide';
      }
      
      if (!element) {
        throw new Error(`Element not found: ${target.nodeId}`);
      }
      
      if (isSlide) {
        return await this.deleteSlide(target.nodeId);
      } else {
        return await this.deleteElementById(target.nodeId);
      }
    }
    
    // Fall back to BaseApplication for other selectors (className, etc.)
    console.log('[PresentationsApplication] Falling back to BaseApplication for delete');
    return super.handleEntityWrite({ operation: 'delete', target });
  }

  /**
   * Delete a slide
   */
  private async deleteSlide(slideId: string): Promise<any> {
    if (!this.yxml || !this.ydoc) {
      throw new Error('Y.js not initialized');
    }
    
    const slide = this.byIdIndex.get(slideId);
    if (!slide) {
      throw new Error(`Slide not found: ${slideId}`);
    }
    
    const slideNumber = slide.attrs?.semantic?.slideNumber;
    
    // Find slide in Y.js
    for (let i = 0; i < this.yxml.length; i++) {
      const element = this.yxml.get(i) as Y.XmlElement;
      if (element.getAttribute('id') === slideId) {
        this.ydoc.transact(() => {
          this.yxml!.delete(i, 1);
          
          // ✅ FIX (Bug #4): Renumber subsequent slides after deletion
          for (let j = i; j < this.yxml!.length; j++) {
            const slide = this.yxml!.get(j) as Y.XmlElement;
            if (slide && slide.getAttribute('className') === 'slide') {
              const semanticStr = slide.getAttribute('semantic') || '{}';
              try {
                const semantic = JSON.parse(semanticStr);
                semantic.slideNumber = j + 1; // Renumber based on new position
                slide.setAttribute('semantic', JSON.stringify(semantic));
              } catch (e) {
                console.warn('[PresentationsApplication] Failed to parse semantic for renumbering:', e);
              }
            }
          }
        }, this.ydoc.clientID);
        
        // ✅ FIX (Bug #2): Rebuild indices after deletion
        await this.buildCoreIndices();
        
        console.log('[PresentationsApplication] ✅ Deleted slide:', {
          slideId,
          slideNumber
        });
        
        return {
          success: true,
          slideId,
          slideNumber,
          message: `Deleted slide ${slideNumber}`
        };
      }
    }
    
    throw new Error('Slide not found in Y.js');
  }

  /**
   * Delete an element by ID
   */
  private async deleteElementById(elementId: string): Promise<any> {
    const result = this.deleteElement(elementId);
    
    if (!result) {
      throw new Error(`Failed to delete element: ${elementId}`);
    }
    
    // ✅ FIX (Bug #2): Rebuild indices after deletion
    await this.buildCoreIndices();
    
    console.log('[PresentationsApplication] ✅ Deleted element:', {
      elementId
    });
    
    return {
      success: true,
      elementId,
      message: `Deleted element ${elementId}`
    };
  }
  
  // ==================== THEME SUPPORT ====================
  
  /**
   * Validate CSS color value
   * ✅ THEME FIX: Ensure colors are valid before applying
   */
  private isValidColor(color: string): boolean {
    if (!color || typeof color !== 'string') return false;
    
    // Check for hex colors (#RGB, #RRGGBB, #RRGGBBAA)
    if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(color)) {
      return true;
    }
    
    // Check for rgb/rgba
    if (/^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(,\s*[\d.]+\s*)?\)$/.test(color)) {
      return true;
    }
    
    // Check for named colors (common ones)
    const namedColors = ['white', 'black', 'red', 'green', 'blue', 'yellow', 'cyan', 'magenta', 
                         'gray', 'grey', 'transparent', 'inherit', 'currentcolor'];
    if (namedColors.includes(color.toLowerCase())) {
      return true;
    }
    
    return false;
  }
  
  /**
   * Apply theme to presentation
   * - Stores theme in entity skeleton
   * - Optionally updates existing elements
   * - New elements will inherit theme defaults
   */
  async applyTheme(theme: PresentationTheme, options: ThemeApplicationOptions = {}): Promise<void> {
    console.log('[PresentationsApplication] 🎨 applyTheme CALLED:', {
      hasTheme: !!theme,
      themeStructure: {
        fontFamily: theme?.defaults?.fontFamily,
        slideBackground: theme?.defaults?.slideBackground,
        textColors: theme?.defaults?.textColors,
        shapeColors: theme?.defaults?.shapeColors,
        fontSizes: theme?.defaults?.fontSizes
      },
      applyToExisting: options.applyToExisting,
      hasYxml: !!this.yxml,
      hasYdoc: !!this.ydoc,
      yxmlLength: this.yxml?.length || 0
    });
    
    if (!this.entityId) {
      throw new Error('Entity not loaded');
    }
    
    // 1. Store theme in entity
    const entity = await entityService.get(this.entityId);
    if (!entity) {
      throw new Error(`Entity not found: ${this.entityId}`);
    }
    
    entity.skeleton.theme = theme;
    
    // ✅ THEME FIX: Add error handling for persistence
    try {
      await entityService.update(this.entityId, { skeleton: entity.skeleton });
      console.log('[PresentationsApplication] ✅ Theme persisted to database');
    } catch (error) {
      console.error('[PresentationsApplication] ❌ Failed to persist theme:', error);
      throw new Error(`Failed to save theme to database: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
    
    // Update cached entity and theme
    this._cachedEntity = entity;
    this._cachedTheme = theme;
    
    console.log('[PresentationsApplication] ✅ Theme stored in entity');
    
    // 2. If applyToExisting, update all elements in Y.js
    console.log('[PresentationsApplication] 🔍 Checking applyToExisting condition:', {
      applyToExisting: options.applyToExisting,
      hasYxml: !!this.yxml,
      hasYdoc: !!this.ydoc,
      willApply: !!(options.applyToExisting && this.yxml && this.ydoc)
    });
    
    if (options.applyToExisting && this.yxml && this.ydoc) {
      console.log('[PresentationsApplication] ✅ Applying theme to existing elements...');
      
      this.ydoc.transact(() => {
        this.applyThemeToElements(theme);
      }, this.ydoc!.clientID);
      
      // Rebuild indices to reflect changes
      await this.buildCoreIndices();
      
      // ✅ THEME FIX: Force save to ensure Y.js changes persist immediately
      await this.forceSave();
      
      console.log('[PresentationsApplication] ✅ Theme applied to existing elements');
    }
    
    console.log('[PresentationsApplication] ✅ Theme applied successfully');
  }
  
  /**
   * Apply theme styles to existing elements
   */
  private applyThemeToElements(theme: PresentationTheme): void {
    if (!this.yxml) return;
    
    console.log('[PresentationsApplication] 🔄 applyThemeToElements START:', {
      totalElements: this.yxml.length,
      slideBackground: theme.defaults.slideBackground
    });
    
    // Apply to all slides
    let slidesUpdated = 0;
    for (let i = 0; i < this.yxml.length; i++) {
      const yslide = this.yxml.get(i) as Y.XmlElement;
      if (!yslide || yslide.getAttribute('className') !== 'slide') {
        console.log(`[PresentationsApplication] ⏭️ Skipping element ${i}: Not a slide`, {
          hasElement: !!yslide,
          className: yslide?.getAttribute('className')
        });
        continue;
      }
      
      // Apply slide background
      const semanticStr = yslide.getAttribute('semantic') || '{}';
      console.log(`[PresentationsApplication] 📝 Processing slide ${i}:`, {
        slideId: yslide.getAttribute('id'),
        semanticStr: semanticStr.substring(0, 100)
      });
      
      try {
        const semantic = JSON.parse(semanticStr);
        const oldBackground = semantic.backgroundColor;
        
        // Always apply theme background when updating existing elements
        semantic.backgroundColor = theme.defaults.slideBackground;
        yslide.setAttribute('semantic', JSON.stringify(semantic));
        
        slidesUpdated++;
        console.log(`[PresentationsApplication] ✅ Updated slide ${i} background:`, {
          slideId: yslide.getAttribute('id'),
          oldBackground,
          newBackground: semantic.backgroundColor
        });
      } catch (e) {
        console.warn('[PresentationsApplication] ❌ Failed to parse semantic:', e);
      }
      
      // Apply to child elements
      for (let j = 0; j < yslide.length; j++) {
        const yelement = yslide.get(j) as Y.XmlElement;
        if (!yelement) continue;
        
        this.applyThemeToElement(yelement, theme);
      }
    }
    
    console.log('[PresentationsApplication] ✅ applyThemeToElements COMPLETE:', {
      totalSlides: this.yxml.length,
      slidesUpdated
    });
  }
  
  /**
   * Apply theme to single element
   */
  private applyThemeToElement(yelement: Y.XmlElement, theme: PresentationTheme): void {
    const className = yelement.getAttribute('className');
    const elementId = yelement.getAttribute('id');
    if (!className) return;
    
    console.log(`[PresentationsApplication] 🎨 Applying theme to element:`, {
      elementId,
      className,
      themeHasTextColors: !!theme?.defaults?.textColors,
      themeHasShapeColors: !!theme?.defaults?.shapeColors,
      themeHasFontSizes: !!theme?.defaults?.fontSizes,
      themeFontFamily: theme?.defaults?.fontFamily
    });
    
    // Parse current style
    const styleStr = yelement.getAttribute('style') || '{}';
    let style: any;
    try {
      style = JSON.parse(styleStr);
    } catch (e) {
      style = {};
    }
    
    const originalStyle = JSON.stringify(style);
    
    // Text elements
    if (className === 'slide-title' || className === 'slide-subtitle' || className === 'slide-text' ||
        className === 'slide-bullet-list' || className === 'slide-numbered-list') {
      
      // Apply text color - ALWAYS apply when theme has colors
      if (theme.defaults.textColors) {
        let themeColor: string;
        if (className === 'slide-title') {
          themeColor = theme.defaults.textColors.title;
        } else if (className === 'slide-subtitle') {
          themeColor = theme.defaults.textColors.subtitle;
        } else {
          themeColor = theme.defaults.textColors.body;
        }
        
        // Validate color before applying
        if (this.isValidColor(themeColor)) {
          const oldColor = style.color;
          style.color = themeColor;
          console.log(`[PresentationsApplication] ✅ Updated text color: ${elementId} (${oldColor} → ${themeColor})`);
        } else {
          console.warn('[PresentationsApplication] Invalid theme color, skipping:', themeColor);
        }
      } else {
        console.warn(`[PresentationsApplication] ⚠️ Theme missing textColors, skipping color update for ${elementId}`);
      }
      
      // Apply font family - ALWAYS apply when theme has fontFamily
      if (theme.defaults.fontFamily) {
        const oldFont = style.fontFamily;
        style.fontFamily = theme.defaults.fontFamily;
        console.log(`[PresentationsApplication] ✅ Updated font family: ${elementId} (${oldFont} → ${theme.defaults.fontFamily})`);
      }
      
      // Apply font size - ALWAYS apply when theme has fontSizes
      if (theme.defaults.fontSizes) {
        const oldSize = style.fontSize;
        if (className === 'slide-title') {
          style.fontSize = theme.defaults.fontSizes.title;
        } else if (className === 'slide-subtitle') {
          style.fontSize = theme.defaults.fontSizes.subtitle;
        } else {
          style.fontSize = theme.defaults.fontSizes.body;
        }
        console.log(`[PresentationsApplication] ✅ Updated font size: ${elementId} (${oldSize} → ${style.fontSize})`);
      }
    }
    
    // Shape elements
    if (className === 'slide-shape') {
      if (theme.defaults.shapeColors) {
        // Validate color before applying
        if (this.isValidColor(theme.defaults.shapeColors.primary)) {
          const oldFill = style.fill;
          style.fill = theme.defaults.shapeColors.primary;
          console.log(`[PresentationsApplication] ✅ Updated shape fill: ${elementId} (${oldFill} → ${style.fill})`);
        } else {
          console.warn('[PresentationsApplication] Invalid theme shape color, skipping:', theme.defaults.shapeColors.primary);
        }
      } else {
        console.warn(`[PresentationsApplication] ⚠️ Theme missing shapeColors, skipping fill update for ${elementId}`);
      }
    }
    
    // Update element
    const newStyle = JSON.stringify(style);
    if (originalStyle !== newStyle) {
      yelement.setAttribute('style', newStyle);
      console.log(`[PresentationsApplication] ✅ Style updated for ${elementId}`, {
        className,
        oldStyle: originalStyle,
        newStyle
      });
    } else {
      console.log(`[PresentationsApplication] ⏭️ No style changes needed for ${elementId}`);
    }
  }
  
  /**
   * Get current theme from entity
   */
  public getCurrentTheme(): PresentationTheme | null {
    // Return cached theme if available
    if (this._cachedTheme) {
      return this._cachedTheme;
    }
    
    // Try to get from cached entity
    if (this._cachedEntity?.skeleton?.theme) {
      this._cachedTheme = this._cachedEntity.skeleton.theme;
      return this._cachedTheme;
    }
    
    return null;
  }
  
  /**
   * Get theme-aware default attributes for element creation
   */
  private getDefaultAttrsWithTheme(className: string, userAttrs: any): any {
    const config = this.getConfig();
    const baseDefaults = config.supportedClasses[className]?.defaultAttrs || {};
    
    // Get current theme
    const theme = this.getCurrentTheme();
    if (!theme) {
      // No theme, just merge base defaults with user attrs
      return { ...baseDefaults, ...userAttrs };
    }
    
    // Apply theme to defaults
    const themedDefaults = JSON.parse(JSON.stringify(baseDefaults)); // Deep clone
    
    // Apply theme to text elements
    if (className === 'slide-title') {
      themedDefaults.style = {
        ...themedDefaults.style,
        color: theme.defaults.textColors.title,
        fontSize: theme.defaults.fontSizes.title,
        fontFamily: theme.defaults.fontFamily
      };
    } else if (className === 'slide-subtitle') {
      themedDefaults.style = {
        ...themedDefaults.style,
        color: theme.defaults.textColors.subtitle,
        fontSize: theme.defaults.fontSizes.subtitle,
        fontFamily: theme.defaults.fontFamily
      };
    } else if (className === 'slide-text' || className === 'slide-bullet-list' || className === 'slide-numbered-list') {
      themedDefaults.style = {
        ...themedDefaults.style,
        color: theme.defaults.textColors.body,
        fontSize: theme.defaults.fontSizes.body,
        fontFamily: theme.defaults.fontFamily
      };
    }
    
    // Apply theme to shapes
    if (className === 'slide-shape') {
      themedDefaults.style = {
        ...themedDefaults.style,
        fill: theme.defaults.shapeColors.primary
      };
    }
    
    // Apply theme to slide background
    if (className === 'slide') {
      themedDefaults.semantic = {
        ...themedDefaults.semantic,
        backgroundColor: theme.defaults.slideBackground
      };
    }
    
    // User-provided attrs override theme
    return this.deepMerge(themedDefaults, userAttrs);
  }
  
  /**
   * Deep merge helper (user attrs override theme)
   * ✅ THEME FIX: Properly handle null values
   */
  private deepMerge(target: any, source: any): any {
    const result = { ...target };
    
    for (const key in source) {
      const sourceValue = source[key];
      
      // ✅ FIX: Handle null explicitly (allows nullifying properties)
      if (sourceValue === null) {
        result[key] = null;
      }
      // Deep merge objects (but not arrays)
      else if (sourceValue && typeof sourceValue === 'object' && !Array.isArray(sourceValue)) {
        result[key] = this.deepMerge(result[key] || {}, sourceValue);
      }
      // Direct assignment for primitives and arrays
      else {
        result[key] = sourceValue;
      }
    }
    
    return result;
  }
  
  // ==================== END THEME SUPPORT ====================
  
  /**
   * Override destroy to flush pending saves
   * ✅ FIXED: Now async to match BaseApplication
   */
  public override async destroy(): Promise<void> {
    console.log('[PresentationsApplication] Destroying...');
    
    // ✅ BaseApplication.destroy() handles:
    // - Force final save (awaited properly)
    // - Cleanup persistence (remove observers, cancel debounced saves)
    // - Collaboration cleanup
    // - Y.Doc cleanup
    await super.destroy();
    
    console.log('[PresentationsApplication] ✅ Destroyed');
  }
}

